import { Component, ElementRef, HostListener, effect, inject, signal, viewChild } from '@angular/core';
import { RouterLink } from '@angular/router';
import { DeskReply, DeskService, DeskTurn } from '../core/desk.service';
import { LanguageService } from '../core/language';

interface Bubble {
  role: 'desk' | 'you';
  text: string;
  actionLabel?: string | null;
  actionPath?: string | null;
}

@Component({
  selector: 'app-desk-assistant',
  imports: [RouterLink],
  templateUrl: './desk-assistant.html',
})
export class DeskAssistant {
  protected readonly desk = inject(DeskService);
  protected readonly lang = inject(LanguageService);
  protected readonly pending = signal(false);
  protected readonly suggestions = signal<string[]>([]);
  protected readonly messages = signal<Bubble[]>([]);
  private readonly log = viewChild<ElementRef<HTMLElement>>('log');
  private readonly field = viewChild<ElementRef<HTMLInputElement>>('ask');
  private replyTimer = 0;

  constructor() {
    this.resetChrome();
    effect(() => {
      this.lang.code();
      const items = this.messages();
      const start = this.lang.t('askStart');
      if (!this.pending() && items.length <= 1 && items[0]?.text !== start) {
        this.resetChrome();
      }
      const open = this.desk.open();
      const queued = this.desk.startWith();
      if (!open) {
        return;
      }
      if (!queued) {
        queueMicrotask(() => this.field()?.nativeElement.focus());
        return;
      }
      this.desk.startWith.set(null);
      queueMicrotask(() => {
        this.field()?.nativeElement.focus();
        this.send(queued);
      });
    });
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    if (this.desk.open()) {
      this.close();
    }
  }

  protected toggle(): void {
    this.desk.open.update((open) => !open);
  }

  protected close(): void {
    this.desk.open.set(false);
  }

  protected onSubmit(event: Event, input: HTMLInputElement): void {
    event.preventDefault();
    this.send(input.value);
    input.value = '';
  }

  protected send(raw: string): void {
    const message = raw.trim();
    if (!message || this.pending()) {
      return;
    }
    const earlier: DeskTurn[] = this.messages()
      .slice(-8)
      .map((bubble) => ({ role: bubble.role, text: bubble.text }));
    this.messages.update((items) => [...items, { role: 'you', text: message }]);
    this.pending.set(true);
    this.scroll();
    const started = Date.now();
    this.desk.ask(message, earlier).subscribe({
      next: (reply) => {
        const wait = Math.max(0, 280 - (Date.now() - started));
        window.clearTimeout(this.replyTimer);
        this.replyTimer = window.setTimeout(() => this.receive(reply), wait);
      },
      error: () => {
        this.pending.set(false);
        this.messages.update((items) => [
          ...items,
          { role: 'desk', text: this.lang.t('askOffline') },
        ]);
        this.suggestions.set(this.starterSuggestions());
        this.scroll();
      },
    });
  }

  private receive(reply: DeskReply): void {
    this.pending.set(false);
    const next = reply.suggestions?.length
      ? reply.suggestions
      : this.starterSuggestions();
    this.suggestions.set(next);
    this.messages.update((items) => [
      ...items,
      {
        role: 'desk',
        text: reply.text,
        actionLabel: reply.actionLabel,
        actionPath: reply.actionPath,
      },
    ]);
    this.scroll();
    queueMicrotask(() => this.field()?.nativeElement.focus());
  }

  private starterSuggestions(): string[] {
    return [this.lang.t('poExplain'), this.lang.t('poReadM'), this.lang.t('poOpenMine')];
  }

  private resetChrome(): void {
    this.suggestions.set(this.starterSuggestions());
    this.messages.set([{ role: 'desk', text: this.lang.t('askStart') }]);
  }

  private scroll(): void {
    setTimeout(() => {
      const node = this.log()?.nativeElement;
      if (node) {
        node.scrollTop = node.scrollHeight;
      }
    });
  }
}
